import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiEdit2, FiSave, FiX, FiDollarSign, FiTrendingUp, FiBarChart2, FiClock } from 'react-icons/fi';
import { clientAPI, reminderAPI, reportAPI } from '../../services/api';
import Modal from '../../components/shared/Modal';

/**
 * Catégories CRM — même liste que ClientListPage
 * Le propriétaire (owner) signe le mandat avec l'agence.
 */
const CLIENT_CATEGORIES = [
  { value: 'owner',    label: 'Propriétaire',  color: '#7c3aed' },
  { value: 'tenant',   label: 'Locataire',     color: '#0ea5e9' },
  { value: 'buyer',    label: 'Acheteur',      color: '#16a34a' },
  { value: 'seller',   label: 'Vendeur',       color: '#d97706' },
  { value: 'investor', label: 'Investisseur',  color: '#be185d' },
  { value: 'prospect', label: 'Prospect',      color: '#6b7280' },
  { value: 'vendor',   label: 'Prestataire',   color: '#64748b' },
];

const CLIENT_TYPES = [
  { value: 'individual', label: 'Particulier' },
  { value: 'company',    label: 'Entreprise'  },
];

const getCategoryBadge = (category) => {
  const cat = CLIENT_CATEGORIES.find(c => c.value === category);
  if (!cat) return null;
  return (
    <span style={{
      background: cat.color + '22',
      color: cat.color,
      border: `1px solid ${cat.color}55`,
      borderRadius: 999,
      padding: '3px 12px',
      fontSize: 13,
      fontWeight: 600,
    }}>
      {cat.label}
    </span>
  );
};

const ClientDetailPage = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [client, setClient]                   = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [tab, setTab]                         = useState('info');
  const [editMode, setEditMode]               = useState(false);
  const [editForm, setEditForm]               = useState({});
  const [showInteraction, setShowInteraction] = useState(false);
  const [interForm, setInterForm]             = useState({ type: 'call', subject: '', content: '', outcome: '' });
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({ title: '', start_time: '', end_time: '', type: 'visit', description: '' });
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderForm, setReminderForm]       = useState({ title: '', remind_at: '', description: '' });
  const [saving, setSaving]                   = useState(false);
  // État financier (propriétaire)
  const [ownerFinancial, setOwnerFinancial]   = useState(null);
  const [ownerFinLoading, setOwnerFinLoading] = useState(false);

  const fetchClient = () =>
    clientAPI.getById(id)
      .then(r => { setClient(r.data); setLoading(false); })
      .catch(() => navigate('/clients'));

  useEffect(() => { fetchClient(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'financier' && client?.client_category === 'owner' && !ownerFinancial) {
      setOwnerFinLoading(true);
      reportAPI.getOwnerFinancial(id)
        .then(r => setOwnerFinancial(r.data))
        .catch(() => {})
        .finally(() => setOwnerFinLoading(false));
    }
  }, [tab, id, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = () => {
    setEditForm({
      first_name:      client.first_name,
      last_name:       client.last_name,
      email:           client.email || '',
      phone:           client.phone || '',
      city:            client.city  || '',
      client_type:     client.client_type     || 'individual',
      client_category: client.client_category || 'prospect',
      source:          client.source || '',
      notes:           client.notes  || '',
    });
    setEditMode(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await clientAPI.update(id, editForm);
      setEditMode(false);
      fetchClient();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    try {
      await clientAPI.addAppointment(id, appointmentForm);
      setShowAppointmentModal(false);
      fetchClient();
    } catch (e) { alert('Erreur lors de la création du rendez-vous'); }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    try {
      await reminderAPI.create({ ...reminderForm, client_id: id });
      setShowReminderModal(false);
      alert('Rappel créé avec succès');
    } catch (e) { alert('Erreur lors de la création du rappel'); }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    await clientAPI.addInteraction(id, interForm);
    setShowInteraction(false);
    fetchClient();
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!client) return null;

  return (
    <div>
      {/* En-tête */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/clients')}>
            <FiArrowLeft />
          </button>
          <div>
            <h1 style={{ margin: 0 }}>{client.first_name} {client.last_name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              {getCategoryBadge(client.client_category)}
              <span className="badge badge-info">
                {CLIENT_TYPES.find(t => t.value === client.client_type)?.label || client.client_type}
              </span>
              {client.city && (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                  📍 {client.city}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editMode ? (
            <button className="btn btn-secondary" onClick={handleEdit}>
              <FiEdit2 /> Modifier
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setEditMode(false)}>
                <FiX /> Annuler
              </button>
              <button className="btn btn-primary" form="edit-client-form" type="submit" disabled={saving}>
                <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="tabs">
        {['info', 'interactions', 'leads', 'rendez-vous', ...(client?.client_category === 'owner' ? ['financier'] : [])].map(t => (
          <button
            key={t}
            className={`tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'financier' ? '💰 État financier' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Onglet INFO ── */}
      {tab === 'info' && (
        <div className="card">
          <div className="card-body">
            {!editMode ? (
              <>
                <div className="detail-row">
                  <div className="detail-label">Catégorie CRM</div>
                  <div className="detail-value">{getCategoryBadge(client.client_category) || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Type de personne</div>
                  <div className="detail-value">
                    {CLIENT_TYPES.find(t => t.value === client.client_type)?.label || client.client_type || '—'}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{client.email || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Téléphone</div>
                  <div className="detail-value">{client.phone || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Ville</div>
                  <div className="detail-value">{client.city || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Source</div>
                  <div className="detail-value">{client.source || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Notes</div>
                  <div className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{client.notes || '—'}</div>
                </div>
                {client.assigned_first_name && (
                  <div className="detail-row">
                    <div className="detail-label">Assigné à</div>
                    <div className="detail-value">{client.assigned_first_name} {client.assigned_last_name}</div>
                  </div>
                )}
              </>
            ) : (
              /* Formulaire d'édition inline */
              <form id="edit-client-form" onSubmit={handleSave}>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Prénom *</label>
                    <input className="form-control" value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Nom *</label>
                    <input className="form-control" value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} required />
                  </div>
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" className="form-control" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Téléphone</label>
                    <input className="form-control" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Ville</label>
                  <input className="form-control" value={editForm.city} onChange={e => setEditForm({ ...editForm, city: e.target.value })} />
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label>
                      Catégorie CRM
                      <span
                        title="Rôle métier du client. Le propriétaire signe le mandat avec l'agence."
                        style={{ marginLeft: 6, cursor: 'help', color: 'var(--text-secondary)', fontSize: 13 }}
                      >ⓘ</span>
                    </label>
                    <select className="form-control" value={editForm.client_category} onChange={e => setEditForm({ ...editForm, client_category: e.target.value })}>
                      {CLIENT_CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Type de personne</label>
                    <select className="form-control" value={editForm.client_type} onChange={e => setEditForm({ ...editForm, client_type: e.target.value })}>
                      {CLIENT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Source</label>
                  <input className="form-control" value={editForm.source} onChange={e => setEditForm({ ...editForm, source: e.target.value })} />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea className="form-control" rows={4} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Onglet INTERACTIONS ── */}
      {tab === 'interactions' && (
        <div className="card">
          <div className="card-header">
            <h2>Interactions ({client.interactions?.length || 0})</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowInteraction(true)}>
              <FiPlus /> Ajouter
            </button>
          </div>
          <div className="card-body">
            {client.interactions?.map(i => (
              <div key={i.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>{i.subject || i.type}</strong>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(i.interaction_date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{i.content}</p>
                {i.outcome && <span className="badge badge-info" style={{ marginTop: 4 }}>{i.outcome}</span>}
              </div>
            ))}
            {(!client.interactions || client.interactions.length === 0) && (
              <div className="empty-state"><p>Aucune interaction enregistrée</p></div>
            )}
          </div>
        </div>
      )}

      {/* ── Onglet LEADS ── */}
      {tab === 'leads' && (
        <div className="card">
          <div className="card-body">
            {client.leads?.map(l => (
              <div key={l.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <strong>{l.property_title || 'Lead #' + l.id.slice(0, 8)}</strong>
                <span
                  className={`badge badge-${l.status === 'won' ? 'success' : l.status === 'lost' ? 'danger' : 'info'}`}
                  style={{ marginLeft: 8 }}
                >
                  {l.status}
                </span>
                {l.budget_max && (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Budget : {parseInt(l.budget_max).toLocaleString('fr-FR')} XOF
                  </p>
                )}
              </div>
            ))}
            {(!client.leads || client.leads.length === 0) && (
              <div className="empty-state"><p>Aucun lead associé</p></div>
            )}
          </div>
        </div>
      )}

      {/* ── Onglet RENDEZ-VOUS ── */}
      {tab === 'rendez-vous' && (
        <div className="card">
          <div className="card-header">
            <h2>Rendez-vous ({client.appointments?.length || 0})</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAppointmentModal(true)}>
              <FiPlus /> Planifier
            </button>
          </div>
          <div className="card-body">
            {client.appointments?.map(a => (
              <div key={a.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <strong>{a.title}</strong>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {new Date(a.start_time).toLocaleString('fr-FR')} — {a.status}
                </p>
              </div>
            ))}
            {(!client.appointments || client.appointments.length === 0) && (
              <div className="empty-state"><p>Aucun rendez-vous planifié</p></div>
            )}
          </div>
        </div>
      )}

      {/* Modal Interaction */}
      <Modal isOpen={showInteraction} onClose={() => setShowInteraction(false)} title="Nouvelle interaction">
        <form onSubmit={handleAddInteraction}>
          <div className="form-group">
            <label>Type</label>
            <select className="form-control" value={interForm.type} onChange={e => setInterForm({ ...interForm, type: e.target.value })}>
              <option value="call">Appel</option>
              <option value="email">Email</option>
              <option value="meeting">Réunion</option>
              <option value="visit">Visite</option>
              <option value="note">Note</option>
            </select>
          </div>
          <div className="form-group">
            <label>Sujet</label>
            <input className="form-control" value={interForm.subject} onChange={e => setInterForm({ ...interForm, subject: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Contenu</label>
            <textarea className="form-control" rows={3} value={interForm.content} onChange={e => setInterForm({ ...interForm, content: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Résultat</label>
            <input className="form-control" value={interForm.outcome} onChange={e => setInterForm({ ...interForm, outcome: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowInteraction(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Enregistrer</button>
          </div>
        </form>
      </Modal>

      {/* Modal Rendez-vous */}
      <Modal isOpen={showAppointmentModal} onClose={() => setShowAppointmentModal(false)} title="Nouveau rendez-vous">
        <form onSubmit={handleAddAppointment}>
          <div className="form-group">
            <label>Titre *</label>
            <input className="form-control" value={appointmentForm.title} onChange={e => setAppointmentForm({ ...appointmentForm, title: e.target.value })} required />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Début *</label>
              <input type="datetime-local" className="form-control" value={appointmentForm.start_time} onChange={e => setAppointmentForm({ ...appointmentForm, start_time: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Fin</label>
              <input type="datetime-local" className="form-control" value={appointmentForm.end_time} onChange={e => setAppointmentForm({ ...appointmentForm, end_time: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label>Type</label>
            <select className="form-control" value={appointmentForm.type} onChange={e => setAppointmentForm({ ...appointmentForm, type: e.target.value })}>
              <option value="visit">Visite</option>
              <option value="signing">Signature</option>
              <option value="call">Appel</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-control" rows={2} value={appointmentForm.description} onChange={e => setAppointmentForm({ ...appointmentForm, description: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowAppointmentModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer</button>
          </div>
        </form>
      </Modal>

      {/* Modal Rappel */}
      <Modal isOpen={showReminderModal} onClose={() => setShowReminderModal(false)} title="Nouveau rappel">
        <form onSubmit={handleAddReminder}>
          <div className="form-group">
            <label>Titre *</label>
            <input className="form-control" value={reminderForm.title} onChange={e => setReminderForm({ ...reminderForm, title: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Date du rappel *</label>
            <input type="datetime-local" className="form-control" value={reminderForm.remind_at} onChange={e => setReminderForm({ ...reminderForm, remind_at: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-control" rows={2} value={reminderForm.description} onChange={e => setReminderForm({ ...reminderForm, description: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowReminderModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer</button>
          </div>
        </form>
      </Modal>

      {/* ══ ONGLET ÉTAT FINANCIER PROPRIÉTAIRE ══ */}
      {tab === 'financier' && client?.client_category === 'owner' && (
        <div style={{ marginTop: 24 }}>
          {ownerFinLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : !ownerFinancial ? (
            <div className="card"><div className="card-body"><p>Aucune donnée financière disponible.</p></div></div>
          ) : (
            <>
              {/* Totaux globaux */}
              <div className="grid-4" style={{ marginBottom: 24 }}>
                {[
                  { icon: <FiDollarSign />, label: 'Total loyers collectés', value: Number(ownerFinancial.totals.total_collected).toLocaleString('fr-FR') + ' XOF', color: '#22c55e', bg: '#dcfce7' },
                  { icon: <FiTrendingUp />, label: 'Commissions agence', value: Number(ownerFinancial.totals.agency_commission).toLocaleString('fr-FR') + ' XOF', color: '#3b82f6', bg: '#dbeafe' },
                  { icon: <FiBarChart2 />, label: 'Net propriétaire', value: Number(ownerFinancial.totals.owner_net).toLocaleString('fr-FR') + ' XOF', color: '#7c3aed', bg: '#ede9fe' },
                  { icon: <FiClock />, label: 'Loyers en attente', value: Number(ownerFinancial.totals.total_pending).toLocaleString('fr-FR') + ' XOF', color: ownerFinancial.totals.total_overdue > 0 ? '#ef4444' : '#f59e0b', bg: ownerFinancial.totals.total_overdue > 0 ? '#fee2e2' : '#fffbeb' },
                ].map((kpi, i) => (
                  <div key={i} className="card">
                    <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, fontSize: 20, flexShrink: 0 }}>
                        {kpi.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{kpi.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Par mandat */}
              <div className="card">
                <div className="card-header"><h2>Détail par bien / mandat</h2></div>
                <div className="table-responsive">
                  <table className="table">
                    <thead><tr>
                      <th>Bien</th><th>N° Mandat</th><th>Type</th><th>Commission</th>
                      <th>Loyers collectés</th><th>Commission agence</th>
                      <th>Net propriétaire</th><th>En attente</th><th>Statut</th>
                    </tr></thead>
                    <tbody>
                      {ownerFinancial.mandates.map(m => (
                        <tr key={m.id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{m.property_title || '—'}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.property_city || ''}</div>
                          </td>
                          <td><code style={{ fontSize: 12 }}>{m.mandate_number}</code></td>
                          <td>{m.type === 'rental' ? 'Location' : m.type === 'management' ? 'Gestion' : 'Vente'}</td>
                          <td style={{ fontWeight: 600, color: '#3b82f6' }}>{m.commission_rate || 0}%</td>
                          <td style={{ fontWeight: 600, color: '#22c55e' }}>
                            {m.financial ? Number(m.financial.total_collected).toLocaleString('fr-FR') + ' XOF' : '—'}
                          </td>
                          <td style={{ color: '#3b82f6' }}>
                            {m.financial ? Number(m.financial.agency_commission).toLocaleString('fr-FR') + ' XOF' : '—'}
                          </td>
                          <td style={{ fontWeight: 600, color: '#7c3aed' }}>
                            {m.financial ? Number(m.financial.owner_net).toLocaleString('fr-FR') + ' XOF' : '—'}
                          </td>
                          <td style={{ color: m.financial?.total_overdue > 0 ? '#ef4444' : '#f59e0b' }}>
                            {m.financial ? Number(m.financial.total_pending).toLocaleString('fr-FR') + ' XOF' : '—'}
                          </td>
                          <td>
                            <span className={`badge badge-${
                              m.status === 'active' ? 'success' : m.status === 'expired' ? 'danger' :
                              m.status === 'cancelled' ? 'danger' : 'warning'
                            }`}>{m.status}</span>
                          </td>
                        </tr>
                      ))}
                      {ownerFinancial.mandates.length === 0 && (
                        <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>Aucun mandat trouvé</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientDetailPage;
